import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import {
  addBrandingVariant,
  archiveBrandingVariant,
  listBrandingVariantSets,
  updateBrandingVariant,
  type BrandingVariantSet,
} from '../../api/brandingVariantApi.js';

/**
 * Branding Variants — the list-box entries under each branding subtype.
 *
 * The rows shown here are the same ones the work-order form offers, so a
 * variant added here is usable immediately. Entries marked Standard come from
 * the platform catalogue: they can be hidden or reordered but not renamed,
 * because the next provisioning run would restore the catalogue wording.
 */
export function BrandingVariantsPage(): JSX.Element {
  const { token } = useAuth();
  const { t } = useTranslation();

  const [sets, setSets] = useState<readonly BrandingVariantSet[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [newCode, setNewCode] = useState('');
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const data = await listBrandingVariantSets(token);
      setSets(data);
      setSelectedId((current) =>
        current && data.some((s) => s.attributeId === current)
          ? current
          : (data[0]?.attributeId ?? null),
      );
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  const selected = useMemo(
    () => sets.find((s) => s.attributeId === selectedId) ?? null,
    [sets, selectedId],
  );

  async function run(action: () => Promise<unknown>, okMessage: string): Promise<void> {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await action();
      setNotice(okMessage);
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  function onAdd(e: FormEvent): void {
    e.preventDefault();
    if (!token || !selected) return;
    void run(
      () =>
        addBrandingVariant(token, selected.attributeId, {
          optionCode: newCode.trim(),
          optionName: newName.trim(),
        }).then(() => {
          setNewCode('');
          setNewName('');
        }),
      t('m17.variants.added'),
    );
  }

  if (loading) return <p>{t('common.loading')}</p>;

  return (
    <main aria-label="branding-variants">
      <h1>{t('m17.variants.title')}</h1>
      <p>{t('m17.variants.intro')}</p>

      {error ? <p role="alert">{error}</p> : null}
      {notice ? <p role="status">{notice}</p> : null}

      {sets.length === 0 ? (
        <p data-testid="no-variant-sets">{t('m17.variants.none')}</p>
      ) : (
        <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }}>
          <nav aria-label="variant-sets" style={{ minWidth: 260 }}>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {sets.map((s) => (
                <li key={s.attributeId}>
                  <button
                    type="button"
                    data-testid={`set-${s.attributeCode}`}
                    aria-current={s.attributeId === selectedId}
                    onClick={() => setSelectedId(s.attributeId)}
                    style={{ width: '100%', textAlign: 'left' }}
                  >
                    {s.attributeCode}
                    <span> ({s.options.length})</span>
                  </button>
                </li>
              ))}
            </ul>
          </nav>

          <section style={{ flex: 1 }} aria-label="variant-list">
            {selected ? (
              <>
                <h2>{selected.attributeCode}</h2>
                <table data-testid="variants-table">
                  <thead>
                    <tr>
                      <th>{t('m17.variants.colCode')}</th>
                      <th>{t('m17.variants.colName')}</th>
                      <th>{t('m17.variants.colOrder')}</th>
                      <th>{t('m17.variants.colActions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selected.options.map((o) => (
                      <tr key={o.optionId} data-testid={`variant-${o.optionCode}`}>
                        <td>{o.optionCode}</td>
                        <td>{o.optionName}</td>
                        <td>{o.displayOrder}</td>
                        <td>
                          <button
                            type="button"
                            disabled={busy}
                            data-testid={`rename-${o.optionCode}`}
                            onClick={() => {
                              const next = window.prompt(t('m17.variants.renamePrompt'), o.optionName);
                              if (!next || !token) return;
                              void run(
                                () => updateBrandingVariant(token, o.optionId, { optionName: next }),
                                t('m17.variants.renamed'),
                              );
                            }}
                          >
                            {t('m17.variants.rename')}
                          </button>
                          <button
                            type="button"
                            disabled={busy}
                            data-testid={`hide-${o.optionCode}`}
                            onClick={() => {
                              if (!token) return;
                              void run(
                                () => archiveBrandingVariant(token, o.optionId),
                                t('m17.variants.hidden'),
                              );
                            }}
                          >
                            {t('m17.variants.hide')}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <form onSubmit={onAdd} aria-label="add-variant">
                  <h3>{t('m17.variants.addTitle')}</h3>
                  <label htmlFor="v-code">{t('m17.variants.colCode')}</label>
                  <input
                    id="v-code"
                    value={newCode}
                    onChange={(e) => setNewCode(e.target.value.toUpperCase())}
                    required
                  />
                  <label htmlFor="v-name">{t('m17.variants.colName')}</label>
                  <input
                    id="v-name"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    required
                  />
                  <button type="submit" disabled={busy} data-testid="add-variant">
                    {t('m17.variants.add')}
                  </button>
                </form>
              </>
            ) : null}
          </section>
        </div>
      )}
    </main>
  );
}
